import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/videa-components/src';
import { useAtom } from 'jotai';

import type { ProviderAndAppointments } from '../../../layout/header/topbar/PrintDownloadDialog';
import type { Appointment } from '../../../react-query/useAppointments';
import { useAppointmentsDetails } from '../../../react-query/useAppointmentsDetails';
import type { GetTaskResponse } from '../../../react-query/useGetTasks';
import type { TVisitSummary } from '../../../react-query/useVisitSummaries';
import { useAppContext } from '../../../utils/hooks/useAppContext';
import { selectDateAtom } from '../atoms';

import { AppointmentsGroupHeader } from './appointmentsGroupHeader';
import { HuddlePrintoutAppointment } from './huddlePrintoutAppointment';

interface Props {
  appointmentIdsToTasks: Record<string, GetTaskResponse>;
  appointments: Appointment[];
  appointmentsByProvider: ProviderAndAppointments[];
  visitSummaries?: TVisitSummary[];
}

export const HuddlePrintoutContent = ({
  appointmentIdsToTasks,
  appointments,
  appointmentsByProvider,
  visitSummaries,
}: Props) => {
  const [selectedDate] = useAtom(selectDateAtom);
  const { user } = useAppContext();
  const unifiedIds = appointments.map(
    ({ appointment }) => appointment.unifiedId,
  );
  const { isLoading: isLoadingTreatments } = useAppointmentsDetails({
    unifiedIds,
  });

  const getVisitSummaryByVisitId = (estimatedVisitId?: number | null) => {
    if (!estimatedVisitId) return undefined;
    return visitSummaries?.find(
      (summary) => estimatedVisitId === summary?.estimatedVisitId,
    );
  };

  const getTasksByUnifiedId = (unifiedId: string) => {
    return appointmentIdsToTasks[unifiedId]?.tasks ?? [];
  };

  if (!selectedDate) return null;

  return (
    <div className="[&_table]:vai-w-full [&_th]:vai-min-w-[150px] [&_td]:vai-min-w-[150px] [&_th:not(:last-child)]:vai-border-r [&_td:not(:last-child)]:vai-border-r [&_th:not(:last-child)]:vai-border-[rgba(237,238,240,1)] [&_td:not(:last-child)]:vai-border-[rgba(237,238,240,1)]">
      {appointmentsByProvider.map(([providerName, appointmentsGroup]) => (
        <div
          className="vai-break-before-page [&:not(:first-of-type)]:vai-mt-[1in]"
          key={providerName}
        >
          <AppointmentsGroupHeader
            date={selectedDate}
            practice={user?.primaryPracticeName ?? ''}
            provider={providerName === 'All' ? undefined : providerName}
          />

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <Trans>Patient</Trans>
                </TableHead>
                <TableHead>
                  <Trans>Treatments</Trans>
                  <p className="vai-typography-small vai-text-muted-foreground">
                    <Trans>(includes planned and scheduled)</Trans>
                  </p>
                </TableHead>
                <TableHead className="vai-leading-4">
                  <Trans>Tasks and Recommendations</Trans>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {appointmentsGroup.map((appointment: any) => (
                <HuddlePrintoutAppointment
                  appointment={appointment}
                  isLoadingTreatments={isLoadingTreatments}
                  key={appointment.appointment.unifiedId}
                  tasks={getTasksByUnifiedId(appointment.appointment.unifiedId)}
                  visitSummary={getVisitSummaryByVisitId(
                    appointment.appointment.estimatedVisitId,
                  )}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      ))}

      <p className="vai-typography-p !vai-mt-4">
        * <Trans>Addressed by a scheduled or completed treatment.</Trans>
      </p>
    </div>
  );
};
